import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FC, FormEvent, useState } from "react";
import { z } from "zod";
import { formatEmail } from "@/utils";
import { useLogin } from "../../queries";
import { WizardName } from "../login-page";
import { LoginStepProps } from "./login-step.types";

const initialFormState = {
  email: "",
  password: "",
};

const formSchema = z.object({
  email: z.string().min(1, "Field is required").email("Wrong format"),
  password: z.string().min(1, "Field is required"),
});

type FormData = z.infer<typeof formSchema>;

const LoginStep: FC<LoginStepProps> = ({
  title,
  description,
  goToNamedStep,
}) => {
  const { login, isLoginLoading } = useLogin({
    goToSetPasswordStep: () => goToNamedStep?.(WizardName.SET_PASSWORD),
  });

  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({
    fullName: false,
    email: false,
    phoneNumber: false,
    password: false,
  });
  const [adminUserFormData, setAdminUserFormData] = useState<Partial<FormData>>(
    {}
  );

  const formData: FormData = {
    ...initialFormState,
    ...adminUserFormData,
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    login(formData);
  };
  const validate = () => {
    const res = formSchema.safeParse(formData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const errors = validate();
  const isFormFilled = Object.values(formData).every((v) => !!v);

  return (
    <Box component="form" width={"100%"} onSubmit={onSubmit}>
      <Stack justifyContent={"flex-start"} width={"100%"}>
        <Typography variant="h4" mt={4}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 4 }}>
          {description}
        </Typography>
      </Stack>

      <Stack
        style={{ padding: 0 }}
        direction={{ xs: "column", sm: "row" }}
        spacing={4}
        marginBottom={3}
      >
        <TextField
          fullWidth
          variant="filled"
          autoComplete="email"
          sx={{ mb: 2 }}
          required
          placeholder="Email"
          label={
            touched.email && errors?.email ? errors?.email?._errors[0] : "Email"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
          error={touched.email ? !!errors?.email?._errors.length : false}
          value={formData.email}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              email: formatEmail(e.target.value),
            }));
            if (touched.email) {
              validate();
            }
          }}
          slotProps={{
            htmlInput: {
              autoCapitalize: "none",
            },
          }}
        />
        <TextField
          fullWidth
          variant="filled"
          autoComplete="password"
          type={showPassword ? "text" : "password"}
          placeholder="password"
          label={
            touched.password && errors?.password
              ? errors?.password?._errors[0]
              : "Password"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
          error={touched.password ? !!errors?.password?._errors.length : false}
          value={formData.password}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              password: e.target.value,
            }));
            if (touched.password) {
              validate();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Button
        fullWidth
        color="secondary"
        variant="contained"
        type="submit"
        loading={isLoginLoading}
        disabled={isLoginLoading || !isFormFilled || !!errors}
      >
        Log In
      </Button>
    </Box>
  );
};

export { LoginStep };
